import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

export type UserType = 'ADMIN' | 'OPERATOR' | 'COMPLIANCE' | 'CUSTOMER' | 'EMPLOYEE' | 'SUPPLIER' | 'SUPER_ADMIN';
export type LayoutType = 'SIDEBAR' | 'TOPBAR' | 'MINIMAL' | 'DASHBOARD_FIRST';

export interface MenuSection {
  id: string;
  label: string;
  order: number;
  collapsible: boolean;
  defaultExpanded?: boolean;
}

export interface MenuItem {
  id: string;
  label: string;
  icon: string;
  path: string;
  order: number;
  visible: boolean;
  sectionId?: string;
  badge?: {
    type: 'count' | 'dot' | 'text';
    value?: string | number;
    color?: string;
  };
}

export interface MenuConfig {
  sections: MenuSection[];
  items: MenuItem[];
}

export interface WidgetConfig {
  widgetId: string;
  position: {
    row: number;
    col: number;
    rowSpan?: number;
    colSpan?: number;
  };
  size?: 'SMALL' | 'MEDIUM' | 'LARGE' | 'FULL';
  settings?: Record<string, unknown>;
}

export interface UserThemeVariant {
  id: string;
  tenantId: string;
  userType: UserType;
  layoutType: LayoutType | null;
  accentColorOverride: Record<string, string> | null;
  buttonVariant: string | null;
  cardVariant: string | null;
  tableVariant: string | null;
  menuConfig: MenuConfig | null;
  widgetConfig: WidgetConfig[] | null;
  createdAt: string;
  updatedAt: string;
}

export interface EffectiveTheme {
  baseTheme: any;
  variant: UserThemeVariant | null;
  resolvedLayout: LayoutType;
  resolvedColors: {
    primary: Record<string, string>;
    accent: Record<string, string>;
    surface: Record<string, string>;
  };
  resolvedVariants: {
    button: string;
    card: string;
    table: string;
  };
  resolvedWidgets: WidgetConfig[];
  resolvedMenuItems: MenuItem[];
}

export interface CreateUserVariantDto {
  userType: UserType;
  layoutType?: LayoutType;
  accentColorOverride?: Record<string, string>;
  buttonVariant?: string;
  cardVariant?: string;
  tableVariant?: string;
  menuConfig?: MenuConfig;
  widgetConfig?: WidgetConfig[];
}

export interface UpdateUserVariantDto {
  layoutType?: LayoutType;
  accentColorOverride?: Record<string, string>;
  buttonVariant?: string;
  cardVariant?: string;
  tableVariant?: string;
  menuConfig?: MenuConfig;
  widgetConfig?: WidgetConfig[];
}

export function useUserVariants(tenantId: string) {
  return useQuery({
    queryKey: ['user-variants', tenantId],
    queryFn: async () => {
      const response = await api.get<UserThemeVariant[]>(`/themes/${tenantId}/variants`);
      return response.data;
    },
    enabled: !!tenantId,
  });
}

export function useUserVariant(tenantId: string, userType: UserType) {
  return useQuery({
    queryKey: ['user-variant', tenantId, userType],
    queryFn: async () => {
      const response = await api.get<UserThemeVariant | null>(
        `/themes/${tenantId}/variants/${userType}`
      );
      return response.data;
    },
    enabled: !!tenantId && !!userType,
  });
}

export function useEffectiveTheme(tenantId: string, userType: UserType) {
  return useQuery({
    queryKey: ['effective-theme', tenantId, userType],
    queryFn: async () => {
      const response = await api.get<EffectiveTheme>(
        `/themes/${tenantId}/effective/${userType}`
      );
      return response.data;
    },
    enabled: !!tenantId && !!userType,
  });
}

export function useCreateUserVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, data }: { tenantId: string; data: CreateUserVariantDto }) => {
      const response = await api.post<UserThemeVariant>(`/themes/${tenantId}/variants`, data);
      return response.data;
    },
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: ['user-variants', tenantId] });
    },
  });
}

export function useUpdateUserVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tenantId,
      variantId,
      data,
    }: {
      tenantId: string;
      variantId: string;
      data: UpdateUserVariantDto;
    }) => {
      const response = await api.put<UserThemeVariant>(
        `/themes/${tenantId}/variants/${variantId}`,
        data
      );
      return response.data;
    },
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: ['user-variants', tenantId] });
    },
  });
}

export function useDeleteUserVariant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ tenantId, variantId }: { tenantId: string; variantId: string }) => {
      await api.delete(`/themes/${tenantId}/variants/${variantId}`);
    },
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: ['user-variants', tenantId] });
    },
  });
}
